/**
 * Reports hub — people, attendance, and group-health reports with simple
 * SVG/CSS charts and CSV export. Each report is gated server-side on its read
 * key (people.read / checkins.read / groups.read); this page surfaces a clean
 * error if the caller lacks it.
 */
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Spinner, cn } from '@fc/ui'
import { useQuery } from '@tanstack/react-query'
import { Download, HeartHandshake, ScanLine, Users } from 'lucide-react'
import { useState } from 'react'
import { PageHeader } from '../../components/page-header'
import { ColumnChart, HorizontalBars } from './charts'
import {
  exportAttendanceCsv,
  exportGroupsCsv,
  exportPeopleCsv,
  getAttendanceReport,
  getGroupsReport,
  getPeopleReport,
} from '../../lib/reporting-api'

type Tab = 'people' | 'attendance' | 'groups'

const TABS: { id: Tab; label: string; icon: typeof Users }[] = [
  { id: 'people', label: 'People', icon: Users },
  { id: 'attendance', label: 'Attendance', icon: ScanLine },
  { id: 'groups', label: 'Groups', icon: HeartHandshake },
]

export function ReportsHub() {
  const [tab, setTab] = useState<Tab>('people')

  return (
    <div className="space-y-6">
      <PageHeader
        title="Reports"
        description="Live aggregations across people, attendance, and groups."
      />

      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Report type">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id)}
            className={cn(
              'inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors',
              tab === t.id
                ? 'border-border-strong bg-card-hover text-fg'
                : 'border-border bg-card text-muted hover:bg-card-hover hover:text-fg',
            )}
          >
            <t.icon className="size-4" />
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'people' && <PeopleReport />}
      {tab === 'attendance' && <AttendanceReport />}
      {tab === 'groups' && <GroupsReport />}
    </div>
  )
}

// ----------------------------- shared -----------------------------

function ReportState({
  isLoading,
  isError,
  error,
}: {
  isLoading: boolean
  isError: boolean
  error: unknown
}) {
  if (isLoading) {
    return (
      <Card className="flex items-center justify-center gap-2 py-20 text-muted">
        <Spinner /> Loading report…
      </Card>
    )
  }
  if (isError) {
    return (
      <Card className="space-y-2 py-20 text-center">
        <p className="text-sm text-danger">Couldn’t load this report.</p>
        <p className="text-xs text-dim">
          {error instanceof Error ? error.message : 'You may not have access to this report.'}
        </p>
      </Card>
    )
  }
  return null
}

function ExportButton({ onExport }: { onExport: () => Promise<void> }) {
  const [busy, setBusy] = useState(false)
  return (
    <Button
      variant="secondary"
      disabled={busy}
      onClick={async () => {
        setBusy(true)
        try {
          await onExport()
        } finally {
          setBusy(false)
        }
      }}
    >
      {busy ? <Spinner /> : <Download />}
      Export CSV
    </Button>
  )
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="font-display text-3xl font-bold tracking-tight tabular-nums">
          {value.toLocaleString()}
        </div>
        <div className="mt-1 text-sm text-muted">{label}</div>
      </CardContent>
    </Card>
  )
}

// ----------------------------- People -----------------------------

function PeopleReport() {
  const q = useQuery({
    queryKey: ['reports', 'people'],
    queryFn: () => getPeopleReport({ months: 12 }),
  })
  const state = <ReportState isLoading={q.isLoading} isError={q.isError} error={q.error} />
  if (q.isLoading || q.isError || !q.data) return state
  const d = q.data
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end">
        <ExportButton onExport={() => exportPeopleCsv({ months: 12 })} />
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard label="Total people" value={d.total} />
        <StatCard
          label="Members"
          value={d.byMembershipStatus.find((s) => s.key === 'member')?.count ?? 0}
        />
        <StatCard label="Campuses" value={d.bySite.length} />
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>By membership status</CardTitle>
          </CardHeader>
          <CardContent>
            <HorizontalBars data={d.byMembershipStatus} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>By campus</CardTitle>
          </CardHeader>
          <CardContent>
            <HorizontalBars data={d.bySite} emptyLabel="No campuses configured." />
          </CardContent>
        </Card>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>New people · last 12 months</CardTitle>
        </CardHeader>
        <CardContent>
          <ColumnChart
            data={d.newOverTime.map((b) => ({ key: b.bucket, label: b.label, count: b.count }))}
          />
        </CardContent>
      </Card>
    </div>
  )
}

// --------------------------- Attendance ---------------------------

function AttendanceReport() {
  const q = useQuery({
    queryKey: ['reports', 'attendance'],
    queryFn: () => getAttendanceReport({}),
  })
  const state = <ReportState isLoading={q.isLoading} isError={q.isError} error={q.error} />
  if (q.isLoading || q.isError || !q.data) return state
  const d = q.data
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end">
        <ExportButton onExport={() => exportAttendanceCsv({})} />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <StatCard label="Total check-ins (90d)" value={d.totalCheckins} />
        <StatCard label="Occurrences with attendance" value={d.rows.length} />
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Check-ins per day</CardTitle>
        </CardHeader>
        <CardContent>
          <ColumnChart
            data={d.byDay
              .filter((b) => b.count > 0)
              .map((b) => ({ key: b.bucket, label: b.label, count: b.count }))}
            emptyLabel="No check-ins in this window."
          />
        </CardContent>
      </Card>
      <Card className="overflow-hidden p-0">
        {d.rows.length === 0 ? (
          <div className="py-16 text-center text-sm text-muted">No attendance recorded yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-dim">
                  <th className="px-5 py-3 font-semibold">When</th>
                  <th className="px-5 py-3 font-semibold">Event</th>
                  <th className="px-5 py-3 font-semibold">Campus</th>
                  <th className="px-5 py-3 text-right font-semibold">Checked in</th>
                  <th className="px-5 py-3 text-right font-semibold">Checked out</th>
                </tr>
              </thead>
              <tbody>
                {d.rows.map((r) => (
                  <tr
                    key={r.occurrenceId}
                    className="border-b border-border-soft last:border-0 hover:bg-card-hover"
                  >
                    <td className="px-5 py-3 text-muted">
                      {new Date(r.occurrenceStartsAt).toLocaleString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                      })}
                    </td>
                    <td className="px-5 py-3 font-medium">{r.eventName}</td>
                    <td className="px-5 py-3 text-muted">{r.siteName ?? '—'}</td>
                    <td className="px-5 py-3 text-right tabular-nums">{r.checkedIn}</td>
                    <td className="px-5 py-3 text-right tabular-nums text-muted">{r.checkedOut}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}

// ----------------------------- Groups -----------------------------

function GroupsReport() {
  const q = useQuery({ queryKey: ['reports', 'groups'], queryFn: () => getGroupsReport({}) })
  const state = <ReportState isLoading={q.isLoading} isError={q.isError} error={q.error} />
  if (q.isLoading || q.isError || !q.data) return state
  const d = q.data
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end">
        <ExportButton onExport={() => exportGroupsCsv({})} />
      </div>
      <div className="grid gap-4 sm:grid-cols-4">
        <StatCard label="Groups" value={d.totalGroups} />
        <StatCard label="Active members" value={d.totalActiveMembers} />
        <StatCard label="Empty groups" value={d.emptyGroups} />
        <StatCard label="Leaderless groups" value={d.leaderlessGroups} />
      </div>
      <Card className="overflow-hidden p-0">
        {d.rows.length === 0 ? (
          <div className="py-16 text-center text-sm text-muted">No groups yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-dim">
                  <th className="px-5 py-3 font-semibold">Group</th>
                  <th className="px-5 py-3 font-semibold">Team</th>
                  <th className="px-5 py-3 font-semibold">Campus</th>
                  <th className="px-5 py-3 text-right font-semibold">Active</th>
                  <th className="px-5 py-3 text-right font-semibold">Pending</th>
                  <th className="px-5 py-3 text-right font-semibold">Leaders</th>
                  <th className="px-5 py-3 font-semibold">Health</th>
                </tr>
              </thead>
              <tbody>
                {d.rows.map((r) => (
                  <tr
                    key={r.groupId}
                    className="border-b border-border-soft last:border-0 hover:bg-card-hover"
                  >
                    <td className="px-5 py-3 font-medium">{r.groupName}</td>
                    <td className="px-5 py-3 text-muted">{r.teamName ?? '—'}</td>
                    <td className="px-5 py-3 text-muted">{r.siteName ?? '—'}</td>
                    <td className="px-5 py-3 text-right tabular-nums">{r.activeMembers}</td>
                    <td className="px-5 py-3 text-right tabular-nums text-muted">
                      {r.pendingMembers}
                    </td>
                    <td className="px-5 py-3 text-right tabular-nums">{r.leaders}</td>
                    <td className="px-5 py-3">
                      {r.activeMembers === 0 ? (
                        <Badge variant="neutral">Empty</Badge>
                      ) : r.leaders === 0 ? (
                        <Badge variant="gold">No leader</Badge>
                      ) : (
                        <Badge variant="success">Healthy</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}
